import {
	anime,
	BabyBedtimePromptSet,
	DreamPromptSet,
	fairytale,
	FairytalesPromptSet,
	handDrawn,
} from "../chatgpt/chatGptTemplates";
import AiPromptSet, { AiImagePromptSetProps, AiPromptSetProps } from "./AiPromptSet";

export enum LogLevel {
	DEBUG = 0,
	INFO = 1,
	WARN = 2,
	ERROR = 3,
}

export enum StoryStatus {
	NOT_STARTED = 0,
	PROMPT_SET_CHOSEN = 10,
	TOPIC_SET = 20,
	TOPIC_ANALYSED = 30,
	TITLE_OPTIONS_SET = 40,
	TITLE_CHOSEN = 50,
	CHARACTERS_IDENTIFIED = 60,
	CHARACTERS_SET = 70,
	TO_FROM_SET = 80,
	DEDICATION_ADDED = 90,
	STORY_WRITTEN = 100,
	STORY_SET = 110,
	IMAGE_PROMPT_SET_CHOSEN = 120,
	CHARACTERS_APPEARANCE_SET = 130,
	VISUAL_DESCRIPTIONS_COVER = 140,
	COVER_IMAGES_GENERATED = 150,
	VISUAL_DESCRIPTIONS_PAGES = 160,
	IMAGES_GENERATED = 170,
	IMAGES_ACCEPTED = 180,
	IMAGES_ENLARGED = 190,
}

export enum TopicChecklist {
	SIMPLE = 0,
	MAIN_CHARACTER = 10,
	MAIN_CHARACTER_NAME = 20,
	CHARACTER_QTY = 30,
	PLOT = 40,
	COPYRIGHT_SAFE = 50
}

export enum OrderStatus {
	CANCELLED = -10,
	CREATED = 0,
	PENDING_PAYMENT = 10,
	PAYMENT_FAILED = 20,
	PAID = 30,
	AWAITING_PRODUCTION = 40,
	IN_PRODUCTION = 50,
	PRODUCTION_FAILED = 60,
	PRODUCTION_DELAYED = 65,
	PRODUCTION_COMPLETE = 70,
	AWAITING_FULFILLMENT = 80,
	FULFILLED = 90,
	SHIPPED = 100,
	OUT_FOR_DELIVERY = 110,
	DELIVERED = 120,
	DELIVERY_FAILED = 130,
	RETURNED = 140,
	REFUNDED = 150,
	IN_DISPUTE = 160,
	REPLACEMENT_SENT = 170,
}

export enum OrderItemStatus{
	CREATED = 0,
	PAID = 1,
	PROCESSED = 2
}

export enum CartItemStatus {
	CREATED = 0,
}

export enum SocketMessageType {
	REQUEST = "request",
	RESPONSE = "response",
	PUSH = "push",
	ERROR = "error",
	FINISH_NEXT = "next",
	FINISH = "finish",
}

export enum ActionVerb {
	HEARTBEAT = "heartbeat",
	CREATE_STORY = "createStory",
	CREATE_USER = "createUser",
	SIGNUP = "signup",
	GET_USER = "getUser",
	GET_ALL_USERS = "getAllUsers",
	UPDATE_USER_PREFERENCES = "updateUserPreferences",
	UPDATE_USER_SHIPPING_ADDRESS = "updateUserShippingAddress",
	SUBMIT_SUPPORT_REQUEST = "submitSupportRequest",
	GET_STORY = "getStory",
	CREATE_ORDER = "createOrder",
	GET_ORDER = "getOrder",
	GET_ORDERS = "getOrders",
	GET_ALL_ORDERS = "getAllOrders",
	GET_ORDER_ITEMS = "getOrderItems",
	GET_STORIES = "getStories",
	GET_ALL_STORIES = "getAllStories",
	CREATE_CART_ITEM = "createCartItem",
	GET_CART_ITEMS = "getCartItems",
	GET_SHIPPING_COSTS = "getShippingCosts",
	GET_PRODUCTS = "getProducts",
	GET_SHIPPING_OPTIONS = "getShippingOptions",
	DELETE_CART_ITEM = "deleteCartItem",
	CREATE_PAYMENT_SESSION = "createPaymentSession",
	GET_PAYMENT_SESSION = "getPaymentSession",
	UPDATE_PAYMENT_STATUS = "updatePaymentStatus",
	UPSCALE_IMAGES = "upscaleImages",
	PUBLISH_BOOK = "publishBook",
	PRINT_ORDER = "printOrder",
	UPDATE_ORDER_STATUS = "updateOrderStatus",
	SET_STORY_PROMPT_SET = "setPromptSet", //1
	SET_STORY_TOPIC = "setTopic", //2
	SET_STORY_ANALYSE_TOPIC = "setAnalyseTopic", //3
	SET_STORY_TITLE_OPTIONS = "setTitleOptions", //4
	SET_STORY_TITLE_CHOICE = "setTitleChoice", //5
	SET_STORY_IDENTIFY_CHARACTERS = "setIdentifyCharacters", //6
	SET_STORY_CHARACTERS = "setCharacters", //7
	SET_STORY_TO_FROM = "setToFrom", //8
	SET_STORY_DEDICATION = "setDedication", //9
	SET_STORY_TEXT = "setText", //10
	SET_STORY_PAGE = "setPageText", //10a
	SET_STORY_PAGE_TEXT_OPTION = "setPageTextOption", //10b
	SET_STORY_IMAGE_PROMPT_SET = "setImagePromptSet", //11
	SET_STORY_CHARACTERS_STYLE = "setCharactersStyle", //12
	SET_STORY_INITIAL_IMAGE_GENERATION = "setInitialImageGeneration", //(batch of 13, 13a)
	SET_STORY_SECONDARY_IMAGE_GENERATION = "setSecondaryImageGeneration", //(batch of 14 and 14a)
	SET_STORY_COVER_VISUAL_DESCRIPTIONS = "setCoverVisualDescriptions", //13
	SET_STORY_VISUAL_DESCRIPTIONS = "setVisualDescriptions", //14
	SET_STORY_COVER_IMAGE = "setCoverImages", //13a
	SET_STORY_PAGE_IMAGE = "setPageImages", //14a
	SET_STORY_PAGE_IMAGE_OPTION = "setPageImageOption", //13/14b
}
export enum HttpMethod {
	GET,
	POST,
	PUT,
	DELETE,
}

export enum PromptSetKey {
	BABY_BEDTIME = "babyBedtime",
	FAIRYTALE = "fairytale",
	DREAM = "dream",
}

export enum ImagePromptSetKey {
	HAND_DRAWN = "handDrawn",
	ANIME = "anime",
	FAIRYTALE = "fairytale",
}

export const PromptSet: { [key: string]: AiPromptSet<AiPromptSetProps> } = {
	[PromptSetKey.FAIRYTALE]: FairytalesPromptSet,
	[PromptSetKey.BABY_BEDTIME]: BabyBedtimePromptSet,
	[PromptSetKey.DREAM]: DreamPromptSet,
};

export const ImagePromptSet: { [key: string]: AiPromptSet<AiImagePromptSetProps> } = {
	[ImagePromptSetKey.HAND_DRAWN]: handDrawn,
	[ImagePromptSetKey.ANIME]: anime,
	[ImagePromptSetKey.FAIRYTALE]: fairytale,
};

export enum AlertVariant {
	ERROR = "danger",
	SUCCESS = "success",
	WARNING = "warning",
	PRIMARY = "primary",
}

export enum ViewTypes {
	LIST = 0,
	FEED = 1,
	TABLE = 2,
}

export enum UserStatus {
	UNCONFIRMED = 0,
	CONFIRMED = 1,
	COMPLETED = 2,
	DELETED = 3,
	PASSWORD_CHANGE_REQUIRED = 4,
}

export enum WsConnectionStatus {
	CONNECTED = 0,
}

export enum SupportRequestStatus {
	OPEN = 0,
}